import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { AppleLogo, CaretDown, Check, GooglePlayLogo } from '@phosphor-icons/react';
import { FREEZE_FOR_CAPTURE, MotionReveal, useSectionSnap } from '@components/motion/scroll-motion';
import { BentoContainer, BentoEyebrow, BentoHeading, BentoSection } from '@components/landing/bento.styles';
import { DownloadPanel } from '@components/landing/download-panel';
import { FadingPhone } from '@components/landing/phone-fade';
import { SiteFooter } from '@components/landing/site-footer';
import { SiteNav } from '@components/landing/site-nav';
import { storeCtaProps } from '@components/landing/store-cta';
import { PRODUCTS, productBySlug, productPath, type Product } from '@components/products/products';
import { ENGINE_KINDS, ENGINE_ORDER, EngineIcon, type EngineKey } from '@components/wish-story/engines';
import { colors } from '@config/colors';
import { media } from '@config/breakpoints';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { FooterCta, HeroCtaRow, PageRoot } from './landing-page.styles';
import { NotFoundPage } from './not-found-page';

/*
 * A product's own page — one per demo that exists (June, 2026-09-23),
 * built like the homepage: its nav, its water, its sections and type, then
 * its Get the app and footer. Between them: the product, the same wish
 * asked of a top LLM, and a section for each thing that made it better —
 * pre-fetched data, domain expert models, assistant capabilities — in the
 * order How we build attaches them.
 *
 * June, 2026-09-23: read the homepage's way — screen-tall sections, each
 * settling into place as it's scrolled to, with a gently bouncing See More
 * that brings the next one up — and the product's own screen recording
 * playing in real time.
 */

type SectionRef = (el: HTMLElement | null) => void;

export const ProductPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const product = productBySlug(slug);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const setSectionRef = (i: number): SectionRef => (el) => {
    sectionRefs.current[i] = el;
  };
  useSectionSnap(sectionRefs);

  // A new product opens at its top, as a new page does.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!product) return undefined;
    const previous = document.title;
    document.title = `${product.name} · The Home CEO`;
    return () => {
      document.title = previous;
    };
  }, [product]);

  if (!product) return <NotFoundPage />;

  return (
    <PageRoot>
      <SiteNav solidAfter={0.2} />
      <ProductHero product={product} sectionRef={setSectionRef(0)} />
      <Compared product={product} sectionRef={setSectionRef(1)} />
      {ENGINE_ORDER.map((kind, i) => (
        <KindSection key={kind} product={product} kind={kind} flip={i % 2 === 1} sectionRef={setSectionRef(2 + i)} />
      ))}
      <MoreWishes current={product} sectionRef={setSectionRef(2 + ENGINE_ORDER.length)} />
      <Section ref={setSectionRef(3 + ENGINE_ORDER.length)}>
        <BentoContainer>
          <DownloadPanel />
        </BentoContainer>
      </Section>
      <SeeMore sections={sectionRefs} />
      <SiteFooter />
    </PageRoot>
  );
};

// ── The product ──────────────────────────────────────────────────────────────

/** The app answering the wish, in real time, over its first frame — still
 *  on that frame for reduced motion. */
const HeroRecording: React.FC<{ screen: Product['screen'] }> = ({ screen }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || still) return;
    // A browser that won't play it leaves the first frame showing.
    video.play().catch(() => undefined);
  }, [still, screen.video]);
  return (
    <video
      ref={ref}
      src={screen.video}
      poster={screen.poster}
      aria-label={screen.label}
      width={540}
      height={1174}
      muted
      playsInline
      loop
      preload="metadata"
    />
  );
};

const ProductHero: React.FC<{ product: Product; sectionRef: SectionRef }> = ({ product, sectionRef }) => (
  <HeroSection ref={sectionRef}>
    <BentoContainer>
      <HeroGrid>
        <MotionReveal>
          <HeroWords>
            <BentoEyebrow>Product</BentoEyebrow>
            <Name>{product.name}</Name>
            <Tagline>{product.tagline}</Tagline>
            <WishBubble>“{product.wish}”</WishBubble>
            <Intro>{product.intro}</Intro>
            <HeroCtaRow style={{ justifyContent: 'flex-start' }}>
              <FooterCta as="a" {...storeCtaProps('ios', 'hero')}>
                <AppleLogo weight="fill" /> App Store
              </FooterCta>
              <FooterCta as="a" {...storeCtaProps('android', 'hero')}>
                <GooglePlayLogo weight="fill" /> Google Play
              </FooterCta>
            </HeroCtaRow>
          </HeroWords>
        </MotionReveal>
        <MotionReveal delay={0.15}>
          <HeroPhone>
            <Phone>
              <HeroRecording key={product.slug} screen={product.screen} />
            </Phone>
          </HeroPhone>
        </MotionReveal>
      </HeroGrid>
    </BentoContainer>
  </HeroSection>
);

// ── Compared to top LLMs ─────────────────────────────────────────────────────

const Compared: React.FC<{ product: Product; sectionRef: SectionRef }> = ({ product, sectionRef }) => (
  <Section ref={sectionRef}>
    <BentoContainer>
      <MotionReveal>
        <BentoEyebrow>Compared to top LLMs</BentoEyebrow>
        <BentoHeading>One wish. Two answers.</BentoHeading>
        <Lead>
          Ask a top general-purpose LLM and you get good general advice. Ask The Home CEO and you get the answer for your
          family — worked out ahead of time, and ready to act on.
        </Lead>
      </MotionReveal>

      <Answers>
        <MotionReveal>
          <Answer $ours={false}>
            <AnswerLabel>Top LLMs, out of the box</AnswerLabel>
            <Asked>{product.wish}</Asked>
            <Reply $ours={false}>{product.compare.llm}</Reply>
          </Answer>
        </MotionReveal>
        <MotionReveal delay={0.12}>
          <Answer $ours>
            <AnswerLabel $ours>The Home CEO</AnswerLabel>
            <Asked>{product.wish}</Asked>
            <Reply $ours>{product.compare.ours}</Reply>
          </Answer>
        </MotionReveal>
      </Answers>

      <MotionReveal>
        <Table>
          <caption>{product.name}: a top LLM out of the box, and The Home CEO</caption>
          <thead>
            <tr>
              <td />
              <th scope="col">Top LLMs, out of the box</th>
              <th scope="col">The Home CEO</th>
            </tr>
          </thead>
          <tbody>
            {product.compare.rows.map((row) => (
              <tr key={row.topic}>
                <th scope="row">{row.topic}</th>
                <td data-label="Top LLMs">{row.llm}</td>
                <td data-label="The Home CEO">
                  <Ours>
                    <Check weight="bold" aria-hidden="true" />
                    {row.ours}
                  </Ours>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </MotionReveal>
    </BentoContainer>
  </Section>
);

// ── What made it better: one section each ────────────────────────────────────

const KindSection: React.FC<{ product: Product; kind: EngineKey; flip: boolean; sectionRef: SectionRef }> = ({
  product,
  kind,
  flip,
  sectionRef,
}) => {
  const made = product.kinds[kind];
  const { label, light, fill, iconInk } = ENGINE_KINDS[kind];
  return (
    <Section ref={sectionRef}>
      <BentoContainer>
        <KindGrid $flip={flip}>
          <MotionReveal>
            <KindWords>
              <KindEyebrow>
                <Mark style={{ background: fill }}>
                  <EngineIcon engine={kind} size={16} color={iconInk} />
                </Mark>
                {label}
              </KindEyebrow>
              <KindHeading>{made.title}</KindHeading>
              <KindBody>{made.body}</KindBody>
            </KindWords>
          </MotionReveal>
          <MotionReveal delay={0.12}>
            <Points style={{ borderTopColor: light }}>
              {made.points.map((point) => (
                <li key={point}>
                  <Tick style={{ background: fill, color: iconInk }}>
                    <Check weight="bold" aria-hidden="true" />
                  </Tick>
                  {point}
                </li>
              ))}
            </Points>
          </MotionReveal>
        </KindGrid>
      </BentoContainer>
    </Section>
  );
};

// ── More wishes ──────────────────────────────────────────────────────────────

const MoreWishes: React.FC<{ current: Product; sectionRef: SectionRef }> = ({ current, sectionRef }) => (
  <Section ref={sectionRef}>
    <BentoContainer>
      <MotionReveal>
        <BentoEyebrow as="h2">More wishes, made better</BentoEyebrow>
      </MotionReveal>
      <MoreGrid>
        {PRODUCTS.filter((p) => p.slug !== current.slug).map((p) => (
          <MotionReveal key={p.slug}>
            <MoreCard to={productPath(p.slug)}>
              <MoreWish>“{p.wish}”</MoreWish>
              <MoreName>{p.name}</MoreName>
              <MoreGo>
                See More <span aria-hidden="true">→</span>
              </MoreGo>
            </MoreCard>
          </MotionReveal>
        ))}
      </MoreGrid>
    </BentoContainer>
  </Section>
);

// ── See More ─────────────────────────────────────────────────────────────────

/** The way down: a pill at the foot of the screen, bouncing gently, that
 *  brings the next section up to settle in place — gone once the last is
 *  on screen. */
const SeeMore: React.FC<{ sections: React.MutableRefObject<(HTMLElement | null)[]> }> = ({ sections }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const [next, setNext] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const update = () => {
      // The first section that starts below the one being read.
      const line = window.innerHeight * 0.4;
      const index = sections.current.findIndex((el) => !!el && el.getBoundingClientRect().top > line);
      setNext(index < 0 ? null : index);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [sections]);
  if (next === null) return null;
  const onMore = () => {
    const here = sections.current[next - 1];
    const view = window.innerHeight;
    // Where the section's words end, not its box: a screen-tall box's
    // centring and padding — and on a phone, the strip under Safari's
    // toolbar — hold nothing left to read.
    const wordsEnd = here ? Math.max(...Array.from(here.children, (el) => el.getBoundingClientRect().bottom)) : 0;
    // Words that run on off the screen (a phone's hero, its comparison) are
    // read on down, a screenful at most, till they end clear of this button;
    // then the next section comes up.
    if (wordsEnd > view + 2) {
      const readTo = (buttonRef.current?.getBoundingClientRect().top ?? view) - 16;
      window.scrollBy({ top: Math.min(view * 0.85, wordsEnd - readTo), behavior: 'smooth' });
      return;
    }
    sections.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <MoreButton ref={buttonRef} type="button" $bounce={!still} onClick={onMore}>
      See More
      <CaretDown weight="bold" aria-hidden="true" />
    </MoreButton>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

/* The homepage's sections: screen-tall, their content centred, settling
   into place as they're scrolled to (useSectionSnap). */
const Section = BentoSection;

/* The first screen. The nav stands in the flow above the hero rather than
   floating over it, as it floats over every later section, so the hero
   takes the screen less the nav and its words need no clearance for it. */
const HeroSection = styled(BentoSection)`
  && {
    padding-top: ${spacing.lg};
    min-height: calc(100vh - var(--nav-foot, 70px));
    min-height: calc(100svh - var(--nav-foot, 70px));
  }
`;

const bob = keyframes`
  0%, 100% { transform: translate(-50%, 0); }
  50% { transform: translate(-50%, -5px); }
`;

/* Where the homepage's Play control stands, in the same glass. */
const MoreButton = styled.button<{ $bounce: boolean }>`
  position: fixed;
  left: 50%;
  bottom: ${spacing.xl};
  z-index: 20;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: ${borderRadius.full};
  border: 1px solid ${colors.overlayWhite80};
  background: ${colors.overlayWhite70};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 10px 28px ${colors.shadowSoft};
  font-family: ${typography.fontFamily.small};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.highlight.dark};
  cursor: pointer;
  ${({ $bounce }) =>
    $bounce &&
    css`
      animation: ${bob} 1.8s ease-in-out infinite;
    `}

  /* Held still under the pointer or the keyboard, for an easy press. */
  &:hover,
  &:focus-visible {
    animation-play-state: paused;
  }

  &:hover {
    background: ${colors.overlayWhite90};
  }

  &:focus-visible {
    outline: 2px solid ${colors.highlight.dark};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  gap: ${spacing['2xl']};
  align-items: center;

  ${media.md} {
    grid-template-columns: 1.3fr 1fr;
    gap: ${spacing['4xl']};
  }
`;

const HeroWords = styled.div`
  max-width: 34em;
`;

const Name = styled.h1`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.md};
`;

const Tagline = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1.25rem, 2.2vw, 1.75rem);
  font-weight: 300;
  line-height: 1.35;
  color: ${colors.highlight.dark};
  margin: 0 0 ${spacing.xl};
`;

/* The wish as a mom writes it in the app: its coral glass bubble. */
const WishBubble = styled.p`
  display: inline-block;
  margin: 0 0 ${spacing.lg};
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: 18px 18px 4px 18px;
  border: 1px solid ${colors.chat.glassRim};
  background: linear-gradient(180deg, ${colors.chat.userGlassTop}, ${colors.chat.userGlassBottom});
  color: ${colors.neutral.extraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 1.0625rem;
  line-height: 1.4;
`;

const Intro = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 1.0625rem;
  line-height: 1.6;
  color: ${colors.highlight.primary};
  margin: 0 0 ${spacing['2xl']};
`;

/* The real screen, in the demos' slate bezel. */
/* The phone, its foot fading away: the phone's own size, centred, so the
   fade falls across its lower quarter. */
const HeroPhone = styled(FadingPhone)`
  width: fit-content;
  margin: 0 auto;
`;

const Phone = styled.div`
  width: min(72vw, 300px);
  margin: 0 auto;
  border-radius: 34px;
  padding: 7px;
  background: ${colors.neutral.extraDark};

  img,
  video {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 27px;
  }
`;

const Lead = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, 1.4vw, 1.1875rem);
  line-height: 1.6;
  color: ${colors.highlight.primary};
  max-width: 44em;
  margin: 0 0 ${spacing['2xl']};
`;

const Answers = styled.div`
  display: grid;
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};

  ${media.md} {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing.xl};
  }
`;

const glass = css`
  border-radius: ${borderRadius['3xl']};
  background: ${colors.overlayWhite70};
  border: 1px solid ${colors.overlayWhite90};
  box-shadow: 0 20px 50px ${colors.shadowSoft};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
`;

const Answer = styled.div<{ $ours: boolean }>`
  ${glass};
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  padding: ${spacing.xl};
  ${({ $ours }) =>
    $ours &&
    css`
      border-color: ${colors.brand.primaryDark};
      box-shadow: 0 24px 60px ${colors.shadowSoft}, inset 0 0 0 1px ${colors.brand.primaryDark};
    `}
`;

const AnswerLabel = styled.div<{ $ours?: boolean }>`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $ours }) => ($ours ? colors.brand.exxxtraDark : colors.neutral.medium)};
`;

const Asked = styled.p`
  align-self: flex-end;
  max-width: 85%;
  margin: 0;
  padding: ${spacing.sm} ${spacing.md};
  border-radius: 16px 16px 4px 16px;
  border: 1px solid ${colors.chat.glassRim};
  background: linear-gradient(180deg, ${colors.chat.userGlassTop}, ${colors.chat.userGlassBottom});
  color: ${colors.neutral.extraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.9375rem;
  line-height: 1.4;
`;

const Reply = styled.p<{ $ours: boolean }>`
  align-self: flex-start;
  max-width: 92%;
  margin: 0;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: 16px 16px 16px 4px;
  background: ${({ $ours }) => ($ours ? colors.brand.primaryLight : colors.neutral.ultraLight)};
  color: ${colors.neutral.extraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 1rem;
  line-height: 1.55;
`;

/* The comparison, row by row. On a phone each row stacks its two
   answers under the topic; wider, it's a table of three columns. */
const Table = styled.table`
  ${glass};
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;

  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  th,
  td {
    text-align: left;
    vertical-align: top;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 0.9375rem;
    line-height: 1.45;
    color: ${colors.neutral.extraDark};
  }

  thead {
    display: none;
  }

  tbody tr {
    display: block;
    padding: ${spacing.lg} ${spacing.xl};
  }

  tbody tr + tr {
    border-top: 1px solid ${colors.neutral.extraLight};
  }

  tbody th {
    display: block;
    font-weight: 500;
    margin-bottom: ${spacing.sm};
  }

  tbody td {
    display: block;
    padding: ${spacing.xs} 0;
  }

  tbody td::before {
    content: attr(data-label);
    display: block;
    font-family: ${typography.fontFamily.small};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${colors.neutral.medium};
  }

  tbody td:first-of-type {
    color: ${colors.neutral.darkMedium};
  }

  ${media.md} {
    thead {
      display: table-header-group;
    }

    tbody tr {
      display: table-row;
      padding: 0;
    }

    /* As specific as the stacked rules above, so these win at this width. */
    thead th,
    thead td,
    tbody th,
    tbody td {
      display: table-cell;
      padding: ${spacing.lg} ${spacing.xl};
    }

    thead th {
      font-family: ${typography.fontFamily.small};
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${colors.neutral.medium};
      padding-bottom: ${spacing.md};
    }

    thead th:last-child {
      color: ${colors.brand.exxxtraDark};
    }

    tbody th,
    tbody td {
      border-top: 1px solid ${colors.neutral.extraLight};
    }

    tbody th {
      width: 26%;
      margin: 0;
    }

    tbody td::before {
      content: none;
    }
  }
`;

const Ours = styled.span`
  display: inline-flex;
  gap: ${spacing.sm};
  align-items: baseline;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${colors.brand.exxxtraDark};
    transform: translateY(2px);
  }
`;

const KindGrid = styled.div<{ $flip: boolean }>`
  display: grid;
  gap: ${spacing.xl};
  align-items: center;

  ${media.md} {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing['4xl']};

    ${({ $flip }) =>
      $flip &&
      css`
        > :first-child {
          order: 2;
        }
      `}
  }
`;

const KindWords = styled.div`
  max-width: 34em;
`;

const KindEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};
`;

const Mark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const KindHeading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.875rem, 3.6vw, 3rem);
  font-weight: 300;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.lg};
  text-wrap: balance;
`;

const KindBody = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, 1.3vw, 1.125rem);
  line-height: 1.65;
  color: ${colors.highlight.primary};
  margin: 0;
`;

const Points = styled.ul`
  ${glass};
  list-style: none;
  margin: 0;
  padding: ${spacing.lg} ${spacing.xl};
  border-top: 4px solid transparent;

  li {
    display: flex;
    align-items: center;
    gap: ${spacing.md};
    padding: ${spacing.md} 0;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 1.0625rem;
    line-height: 1.45;
    color: ${colors.neutral.extraDark};
  }

  li + li {
    border-top: 1px solid ${colors.neutral.extraLight};
  }
`;

const Tick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: ${colors.neutral.extraDark};

  svg {
    width: 13px;
    height: 13px;
  }
`;

const MoreGrid = styled.div`
  display: grid;
  gap: ${spacing.lg};

  ${media.md} {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing.xl};
  }
`;

const MoreCard = styled(Link)`
  ${glass};
  display: block;
  padding: ${spacing.xl};
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${colors.highlight.dark};
    outline-offset: 3px;
  }
`;

const MoreWish = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-style: italic;
  font-size: 1rem;
  line-height: 1.45;
  color: ${colors.highlight.primary};
  margin: 0 0 ${spacing.sm};
`;

const MoreName = styled.h3`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.5rem, 2.4vw, 2rem);
  font-weight: 500;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.md};
`;

const MoreGo = styled.span`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${colors.accent.coral.exxxtraDark};
`;
